import { frontendURL } from '../../../helper/URLHelper';
import store from '../../../store/index';
import {
  resolveSubscriptionAccess,
  hasHighTierAccess,
} from '../../../helper/subscriptionHelper';
import { hasOrganizationAccess } from '../../../helper/organizationAccessHelper';
import { withRealEstateRedirect } from '../../../helper/moduleGuards';
import Index from './Index.vue';
import BranchIndex from './branches/Index.vue';
import ServiceIndex from './services/Index.vue';
import StaffIndex from './staffs/Index.vue';
// import BranchDetails from '../branches/components/viewBranch.vue';
import BranchDetails from './branches/BranchDetails.vue';
import StaffDetails from './staffs/StaffDetails.vue';
import ConditionIndex from './conditions/Index.vue';
import ServiceDetails from './services/ServiceDetails.vue';
import BranchServiceDetails from './branches/BranchServiceDetails.vue';
import BranchStaffDetails from './branches/BranchStaffDetails.vue';
import PromotionIndex from './promotions/Index.vue';
import StaffCalendarIndex from './staffCalendar/Index.vue';

// Use optimized subscription guard with cached data
const routerGuardInner = async (to, from, next) => {
  const accountId = to.params.accountId;

  // Allow access in development environment, for legacy organization
  // accounts, or accounts with the organization feature flag enabled
  if (hasOrganizationAccess(store, accountId)) {
    // Check subscription status
    const { hasActiveSubscription, hasError } = await resolveSubscriptionAccess(
      store,
      accountId
    );
    if (hasError) {
      return next({
        name: 'subscription_error',
        params: { accountId },
        query: { redirect: to.fullPath },
      });
    }
    if (!hasActiveSubscription) {
      return next({ name: 'pricing_table', params: { accountId } });
    }
    return next();
  }

  return next({ name: 'dashboard' });
};

// Real Estate accounts see their own catalogue/agents/appointments pages
// under real_estate/ instead of the Organization hub — redirect them away
// rather than showing both.
const routerGuard = withRealEstateRedirect(routerGuardInner, store);

// Module restriction guard for healthcare-specific features
const moduleRestrictedGuardInner = async (to, from, next) => {
  const accountId = to.params.accountId;

  // 1. Check account access (development, legacy accounts, or feature flag)
  if (!hasOrganizationAccess(store, accountId)) {
    return next({ name: 'dashboard' });
  }

  // 2. Check subscription status
  const { hasActiveSubscription, hasError } = await resolveSubscriptionAccess(
    store,
    accountId
  );
  if (hasError) {
    return next({
      name: 'subscription_error',
      params: { accountId },
      query: { redirect: to.fullPath },
    });
  }
  if (!hasActiveSubscription) {
    return next({ name: 'pricing_table', params: { accountId } });
  }

  // 3. Check module name and tier for healthcare-specific features
  const moduleName = store.getters['stripe/getModuleName'];

  // Only allow access if tier is enterprise/pro AND module is Healthcare
  if (!hasHighTierAccess(store) || moduleName !== 'healthcare') {
    return next({ name: 'organization' });
  }

  return next();
};

// Real Estate accounts fall through the account-restriction check into
// `next({ name: 'organization' })` below, which the wrapped `routerGuard`
// above already redirects to the dashboard — wrapping here too sends them
// straight there instead of bouncing through `organization` first.
const moduleRestrictedGuard = withRealEstateRedirect(
  moduleRestrictedGuardInner,
  store
);

// Build routes array conditionally
const buildRoutes = () => {
  const routes = [
    {
      path: frontendURL('accounts/:accountId/organization'),
      component: Index,
      name: 'organization',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/branches'),
      component: BranchIndex,
      name: 'organization.branches',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/branches/:branchId'),
      component: BranchDetails,
      name: 'organization.branches.details',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL(
        'accounts/:accountId/organization/branches/:branchId/services/:serviceId'
      ),
      component: BranchServiceDetails,
      name: 'organization.branches.details.service',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL(
        'accounts/:accountId/organization/branches/:branchId/staff/:staffId'
      ),
      component: BranchStaffDetails,
      name: 'organization.branches.details.staff',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/services'),
      component: ServiceIndex,
      name: 'organization.services',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/services/:serviceId'),
      component: ServiceDetails,
      name: 'organization.services.details',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/staffs'),
      component: StaffIndex,
      name: 'organization.staffs',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/staffs/:staffId'),
      component: StaffDetails,
      name: 'organization.staffs.details',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/conditions'),
      component: ConditionIndex,
      name: 'organization.conditions',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: moduleRestrictedGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/promotions'),
      component: PromotionIndex,
      name: 'organization.promotions',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: moduleRestrictedGuard,
    },
    {
      path: frontendURL('accounts/:accountId/organization/staff-calendar'),
      component: StaffCalendarIndex,
      name: 'organization.staff-calendar',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: routerGuard,
    },
  ];

  return routes;
};

export default {
  routes: buildRoutes(),
};
